import LiveKitActionsBarContainer from './LiveKitActionsBarContainer';
import LiveKitCallHeader from './LiveKitCallHeader';
import LiveKitLocalPreview from './LiveKitLocalPreview';
import useActiveDevice from './useActiveDevice';
import useAnnounceInCall from './useAnnounceInCall';
import useAnnounceReady from './useAnnounceReady';
import useAudioOutputPreference from './useAudioOutputPreference';
import useDisconnectOnUnload from './useDisconnectOnUnload';
import useDoctorPresence from './useDoctorPresence';
import usePublishLocalTracks from './usePublishLocalTracks';

import TelemedicineContext from '../../../context/telemedicine';
import useDeviceList from '../../../hooks/useDeviceList';
import { isFrontFacingDevice } from '../../../utils/media-devices';
import { formatUserName } from '../../../utils/utils';

import { Flex, Icon, Loading, Text } from '@amigoapp/doca-react';
import { RoomAudioRenderer, useConnectionState, useTracks, VideoTrack } from '@livekit/components-react';
import classNames from 'classnames';
import { ConnectionState, Track } from 'livekit-client';
import { useContext } from 'react';

const LiveKitVideoRoom = () => {
	const previewVideoTrack = usePublishLocalTracks();

	useDisconnectOnUnload();
	useAnnounceReady();
	useAnnounceInCall();
	useAudioOutputPreference();

	const { telemedicine } = useContext(TelemedicineContext);

	const { hasDoctorJoined, isDoctorConnectionLost } = useDoctorPresence();

	const connectionState = useConnectionState();
	const loading = connectionState !== ConnectionState.Connected;

	const cameraDevices = useDeviceList('videoinput', true);
	const { activeDeviceId: activeCameraId } = useActiveDevice('videoinput');
	const isSelfViewMirrored = isFrontFacingDevice(cameraDevices, activeCameraId);

	const cameras = useTracks([Track.Source.Camera]);
	const localCamera = cameras.find(camera => camera.participant.isLocal);
	const remoteCamera = cameras.find(camera => !camera.participant.isLocal);

	const selfViewClassName = classNames('livekit-video', { 'lk-self-view--mirrored': isSelfViewMirrored });

	const providerName = formatUserName(telemedicine.user.title, telemedicine.user.name);

	const renderStage = () => {
		if (remoteCamera) {
			return <VideoTrack className="livekit-video" trackRef={remoteCamera} />;
		}

		if (isDoctorConnectionLost) {
			return (
				<Flex align="center" className="doctor-stage-message" direction="col" gap={3} justify="center">
					<Icon name="wifi-slash" type="light" />
					<Text align="center" weight="bold">O médico perdeu a conexão</Text>
					<Text align="center" size="small" weight="light">
						Aguarde, ele deve retornar em instantes.
					</Text>
				</Flex>
			);
		}

		if (loading) {
			return null;
		}

		if (!hasDoctorJoined) {
			return (
				<Flex align="center" className="doctor-stage-message" direction="col" gap={3} justify="center">
					<span className="lk-awaiting-doctor__halo">
						<Icon name="user-doctor" type="solid" />
					</span>
					<Text align="center" weight="bold">Aguardando {providerName}</Text>
					<Text align="center" size="small" weight="light">
						Você já está na consulta. Assim que o profissional entrar, o vídeo aparece aqui.
					</Text>
				</Flex>
			);
		}

		return (
			<Flex align="center" className="doctor-stage-message" direction="col" gap={3} justify="center">
				<Loading />
				<Text align="center" size="small" weight="light">Aguardando o vídeo do médico</Text>
			</Flex>
		);
	};

	return (
		<Flex className="lk-call" direction="col" flex={1}>
			{loading && <Loading className="absolute" />}
			<div className="doca-flex doca-flex-1 doca-justify-center" id="doctor-tracks">
				<LiveKitCallHeader providerName={providerName} />

				{renderStage()}
			</div>
			<div id="patient-tracks">
				{loading && !previewVideoTrack && <Loading />}
				{localCamera
					? <VideoTrack className={selfViewClassName} trackRef={localCamera} />
					: previewVideoTrack && <LiveKitLocalPreview className={selfViewClassName} track={previewVideoTrack} />}
				<span className="patient-tracks__label">Você</span>
			</div>
			<RoomAudioRenderer />
			<LiveKitActionsBarContainer />
		</Flex>
	);
};

export default LiveKitVideoRoom;
